import React, { useState, useContext, useEffect, useRef } from 'react';
import { APP_VERSION } from "./version";
import { urlBase64ToUint8Array } from './components/utils';
import TabContext from './components/TabContext';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import SidebarRight from './components/SidebarRight';
import UserPlantsForm from './components/UserPlantsForm';
import CropProfile from './components/CropProfile';
import ThreeMonthTaskPlanner from './components/ThreeMonthTaskPlanner';
import Preserve from './components/Preserve';
import AuthForm from './components/AuthForm';
import AddGardenerForm from './components/AddGardenerForm';
import './index.css';
import './App.css';
import './components/Buttons.css';
import HomeBaseForm from './components/HomeBaseForm';
import LandingPage from './components/LandingPage';
import LandingHeader from './components/LandingHeader';
import LandingSidebar from './components/LandingSidebar';
import AboutContent from './components/AboutForm';
import GardenerOnboard from './components/GardenerOnboard';
import GardenJournal from './components/GardenJournal';
import AdminDashboard from './components/AdminDashboard'; 
import SupplierSystem from './components/SupplierSystem';
import FarmstandSystem from './components/FarmstandSystem';
import Orders from './components/Orders';
import api from './api';
import DemoTour from './components/DemoTour';
import HelpPage from './components/HelpPage';

console.log("App version:", APP_VERSION);

function App() {

    // restore token and user from localstorage
    const [token, setToken] = useState(localStorage.getItem('token'));

    const [user, setUser] = useState(() => {
        const saved = localStorage.getItem('user');
        return saved ? JSON.parse(saved) : null;
    });
    const [isLoggedIn, setIsLoggedIn] = useState(!!token); 


    const [plants, setPlants] = useState([]);
    const [userPlantData, setUserPlantData] = useState([]);
    const [gardeners, setGardeners] = useState([]);
    const [report, setReport] = useState([]);
    const [currentGardener, setCurrentGardener] = useState(null);
    const [credentials, setCredentials] = useState({ username: '', password: '' });
    const [errorMessage, setErrorMessage] = useState('');
    const [currentTab, setCurrentTab] = useState(token ? 'Home' : 'LandingPage');
    const [selectedVariety, setSelectedVariety] = useState(null);
    const [selectedCrop, setSelectedCrop] = useState(null);
    const [isRegistering, setIsRegistering] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    //used by forgot password
    const [newPassword, setNewPassword] = useState('');
    //used by mustResetPassword
    const [forcedPassword, setForcedPassword] = useState('');
    const [runTour, setRunTour] = useState(false);
    const [isDemoMode, setIsDemoMode] = useState(false);
    const demoTriggeredRef = useRef(false);
    const [showDemoPrompt, setShowDemoPrompt] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showHelp, setShowHelp] = useState(false);
    const [isLocked, setIsLocked] = useState(false); // state when the scheduler is running
    const [isLockedOutMode, setIsLockedOutMode] = useState(false); // trial ended or subscription canceled


    const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const month = monthNames[now.getMonth()];
    const day = String(now.getDate()).padStart(2, '0');
    const year = now.getFullYear();

    return `${month}-${day}-${year}`;  // "Jan-01-2026"
    }); 
    // const [currentDate, setCurrentDate] = useState('Apr-22-2026');

    const initialGardenerData = {
        name: '',
        email: '',
        phone: '',
        locationID: '',
        familysize: '',
        experienceLevel: '',
        isNewUser: '',
        city: '',
        province: '',
        country: '',
        plantID: '',
        plantType: '',
        userid: '',
        scheduleTable:'',
        emailVerified: false
    };

    useEffect(() => {
        if (isDemoMode || demoTriggeredRef.current) return;
        if (!token || !user) return;

        const routeUser = async () => {
            try {
                // 1. Validate token
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const decodedToken = JSON.parse(atob(base64));

                if (decodedToken.exp * 1000 < Date.now()) {
                    handleLogout();
                    return;
                }

                // 2. Supplier therefore go straight to SupplierSystem
                if (user.isSupplier) {
                    setCurrentTab('Supplier');
                    return;
                }

                // 3. Farmstand therefore go straight to FarmstandSystem
               // if (user.isFarmstand) {
                    //setCurrentTab('Farmstand');
                   // return;
                //}

                // 4. Gardener therefore fetch gardener
                if (user.isGardener) {
                    const g = await fetchCurrentGardener(false);

                    if (g?.isNewUser) {
                        handleTabChange('Gardener');
                    } else {
                        handleTabChange('Home');
                    }
                }

            } catch (err) {
                console.error("Invalid token", err);
                handleLogout();
            }
        };

        routeUser();
    }, [token, user, isDemoMode]);

    const fetchGardenerPlants = async (gardenerID) => {
        try {
            const response = await api.get(`/gardenerPlants/${gardenerID}`);
            setPlants(response.data);
        } catch (err) {
            console.error('Error fetching plants:', err);
        }
    };
    const fetchUserPlantData = async (gardenerID) => {
        try {
            const response = await api.get(`/userPlants/${gardenerID}`);
            setUserPlantData(response.data);
        } catch (err) {
            console.error('Error fetching user plant data:', err);
        }
    };

    const fetchCurrentGardener = async (demoMode = false) => {

        try {
            setUserPlantData([]); // Clear stale data immediately
            const response = await api.get('/gardeners/current', {
                params: { demoMode }
            });

            console.log("Current gardener from backend:", response.data);

            const data = response.data;
            setCurrentGardener(data);
            fetchGardenerPlants(data.id);
            fetchUserPlantData(data.id);
            return data;
        } catch (err) {
            console.error('Error fetching current gardener:', err);
        }
    };

    const prevGardenerId = useRef(null);


    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;

        navigator.serviceWorker.addEventListener('controllerchange', () => {
            console.log('Service worker changed � reloading once');
            window.location.reload();
        });
    }, []);

    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;
        if (!currentGardener?.id) return; // only run after login

        const isStandalone =
            window.matchMedia('(display-mode: standalone)').matches ||
            window.navigator.standalone === true;

        if (!isStandalone) {
            console.log('Not in standalone mode � skipping self-heal');
            return;
        }

        const selfHealSubscription = async () => {
            try {
                const registration = await navigator.serviceWorker.ready;

                //  Get browser subscription
                let browserSub = await registration.pushManager.getSubscription();

                //  Fetch stored subscription from backend
                const res = await fetch(`/api/main/get-subscription?gardenerID=${currentGardener.id}`);
                const backendSub = await res.json(); // expected { endpoint, keys, ... } or null
                const backendEndpoint = backendSub?.endpoint;

                //  Compare
                if (browserSub && backendEndpoint === browserSub.endpoint) {
                    console.log('Subscription valid and matches backend � no action needed');
                    return;
                }

                // Heal if mismatch or missing
                if (browserSub) {
                    console.log('Browser subscription invalid or outdated � unsubscribing');
                    await browserSub.unsubscribe();
                }

                if (Notification.permission !== 'granted') {
                    console.log('Push permission not granted � skipping self-heal');
                    return;
                }


                console.log('Creating new subscription');
                const newSub = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(process.env.REACT_APP_VAPID_PUBLIC_KEY)
                });

                //  Save new subscription to backend
                await fetch('/api/save-subscription', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        gardenerID: currentGardener.id,
                        subscription: newSub
                    })
                });
                console.log('New subscription saved to backend');

            } catch (err) {
                console.error('Service Worker / Subscription self-heal error:', err);
            }
        };

        selfHealSubscription();
    }, [currentGardener]); // only run when gardener logs in / changes


    useEffect(() => {
        if (!currentGardener) return;

        // Only clear selection if the gardener ID *actually* changed
        if (currentGardener.id !== prevGardenerId.current) {
            setSelectedCrop(null);
            setSelectedVariety(null);
            prevGardenerId.current = currentGardener.id;
        }
    }, [currentGardener]);

useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripeStatus = params.get("stripe");

    if (stripeStatus === "account-complete") {
        // Prevent onboarding from resetting
        sessionStorage.setItem("stripeOnboardingComplete", "true");
    }

    if (stripeStatus === "verification-complete") {
        // Prevent onboarding from resetting
        sessionStorage.setItem("stripeVerificationComplete", "true");
    }

    if (stripeStatus === "success") {
        console.log("Subscription created successfully")
    }

    if (stripeStatus === "cancel") {
        console.log("Stripe checkout canceled");
    }
}, []);


   /* useEffect(() => {

          if (isDemoMode || demoTriggeredRef.current) return; //  skip if demo mode is active
            const fetchCurrentGardenerOnMount = async () => {
            if (token) {
                try {
                    const base64Url = token.split('.')[1];
                    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                    const decodedToken = JSON.parse(atob(base64));
                    if (decodedToken.exp * 1000 < Date.now()) {
                        handleLogout();
                    } else {
                        await fetchCurrentGardener(false);
                    }
                } catch (err) {
                    console.error('Invalid token');
                    handleLogout();
                }
            }
        };
        fetchCurrentGardenerOnMount();
    }, [token, isDemoMode]); */

    useEffect(() => {
        const interceptor = api.interceptors.response.use(
            res => res,
            err => {
                if (err.response?.status === 401) {
                    console.log('Token expired � logging out');
                    handleLogout(); // This resets state and switches to LandingPage
                }
                return Promise.reject(err);
            }
        );

        return () => {
            api.interceptors.response.eject(interceptor);
        };
    }, []);

    useEffect(() => {
    const fetchLockFlag = async () => {
        try {
            const res = await api.get('/auth/systemflags/cronMaintenance');
            const data = res.data;

            console.log("APP RECEIVED: ", data);

            setIsLocked(data.cronMaintenance);
        } catch (err) {
            console.error("Failed to load cronMaintenance flag", err);
        }
    };

    fetchLockFlag();
}, []);


    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentTab]);

    useEffect(() => {
        const path = window.location.pathname;
        if (path === '/reset-password') {
            setIsResetting(true);   // flip the flag
            setCurrentTab('LandingPage'); // show LandingPage, which already has AuthForm inline
        }
    }, []);

    useEffect(() => {
  if (isResetting) {
    const token = new URLSearchParams(window.location.search).get('token');
    if (token) {
      const fetchUsername = async () => {
        try {
          const res = await api.get(`/auth/reset-info?token=${token}`);
          setCredentials(prev => ({ ...prev, username: res.data.username }));
        } catch (err) {
          console.error('Error fetching reset info:', err);
          setErrorMessage('Invalid or expired reset link.');
        }
      };
      fetchUsername();
    }
  }
}, [isResetting]);

    useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e); // store globally
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCredentials({ ...credentials, [name]: value });
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        demoTriggeredRef.current = false;
        setIsDemoMode(false);

        try {
            const response = await api.post('/auth/register', credentials);
            const data = response.data;

            localStorage.setItem('token', data.token);
            setUser(data.user);

            setToken(data.token);
            setIsRegistering(true);
            setIsLoggedIn(true);
            //console.log("about to fetch the new gardener so we can set the tab");
            //const gardener = await fetchCurrentGardener(isDemoMode);
            //console.log("just fetched the new gardener so we can set the tab: ", gardener);
            //setCurrentTab(gardener.isNewUser ? 'Gardener' : 'Home');
        } catch (err) {
            const raw = err.response?.data;
            const message =
            typeof raw === 'string'
            ? raw
            : raw?.message || 'An error occurred. Please try again later.';
            setErrorMessage(message);
            console.error('Registration error:', message);
        }
    };

    const handleResetPassword = async () => {
        console.log('handleResetPassword called with username:', credentials.username);

        // 1. Require username
        if (!credentials.username || credentials.username.trim() === '') {
            setErrorMessage('Please enter your username first.');
            return;
        }

        try {
            // 2. Ask backend if username exists and has an email
            const check = await api.post('/auth/check-username', {
                username: credentials.username
            });

            if (!check.data.exists) {
                setErrorMessage('No account found with that username.');
                return;
            }

            if (!check.data.hasEmail) {
                setErrorMessage('This account does not have an email on file.');
                return;
            }

            // 3. Safe to initiate reset
            await api.post('/auth/forgot-password', {
                username: credentials.username
            });

            setErrorMessage('Check your email for a reset link.');
        } catch (err) {
            setErrorMessage('Error initiating password reset.');
        }
    };

    const handleConfirmResetPassword = async () => {
        // Grab the reset token from the URL query string
        const token = new URLSearchParams(window.location.search).get('token');

        if (!token) {
            setErrorMessage('Reset token missing.');
            return;
        }

        try {
            // Call your backend reset endpoint
            await api.post('/auth/reset-password', {
                token,
                newPassword: credentials.password,
            });

            setErrorMessage('Password reset successful. You can now sign in.');
            setIsResetting(false);
            setCurrentTab('Home');
        } catch (err) {
            const raw = err.response?.data;
            const message =
                typeof raw === 'string'
                    ? raw
                    : raw?.message || 'Error resetting password. Please try again.';
            setErrorMessage(message);
            console.error('Reset error:', message);
        }
    };

const handleForcedPasswordUpdate = async () => {
  try {

    console.log("handle forced password: ", forcedPassword);

    await api.post('/auth/ForcedPasswordUpdate', {
      userName: credentials.username,
      newPassword: forcedPassword
    });

    setForcedPassword('');
    setErrorMessage('Password updated. Please log in with your new password.');
    setCurrentTab('LandingPage');

  } catch (err) {
    setErrorMessage('Error updating password.');
  }
};


    const handleLogin = async () => {
        setErrorMessage('');
        demoTriggeredRef.current = false;
        setIsDemoMode(false);

        try {
            const response = await api.post('/auth/login', credentials);
            const data = response.data;
            //console.log("DATA.USER ",data.user);

            // -----------------------------------------
            // 1. Forced password reset
            // -----------------------------------------
            if (data.user.mustResetPassword === 1 || data.user.mustResetPassword === true) {

                //prefill username
                setCredentials(prev => ({
                    ...prev,
                    username: data.user.username
                }));

                // pop up a field to let them enter a new password... save it and then normal flow
                setCurrentTab('ForcedReset');
                return;
            }

            // -----------------------------------------
            // 2. Normal login: store token + user
            // -----------------------------------------
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user));
            setToken(data.token);
            setUser(data.user);
            setIsLoggedIn(true);

            // -----------------------------------------
            // 3. Supplier / Farmstand routing
            // -----------------------------------------
         //   if (data.user.isSupplier) {
         //       return; // enter <SupplierSystem ... />
         //   }

         //   if (data.user.isFarmstand) {
         //       return; // enter <FarmstandSystem ... />
         //   }

            // -----------------------------------------
            // 4. load gardener
            // -----------------------------------------
        //    const gardener = await fetchCurrentGardener(isDemoMode);

            // Route based on isNewUser
         //   if (gardener?.isNewUser) {
          //      setCurrentTab('Gardener');
          //  } else {
          //      setCurrentTab('Home');
          //  }
        } catch (err) {
            const raw = err.response?.data;
            const message =
            typeof raw === 'string'
            ? raw
            : raw?.message || 'An error occurred. Please try again later.';
            setErrorMessage(message);
            console.error('Registration error:', message);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setCurrentGardener(initialGardenerData); // Clear gardener data
        setPlants([]);
        setUserPlantData([]);
        setIsLoggedIn(false); // Set login status to false
        setIsRegistering(false);
        demoTriggeredRef.current = false;
        setIsDemoMode(false);

        setCurrentTab('LandingPage');
    };

    const addPlant = (newPlant) => {
        setPlants([...plants, newPlant]);
    };

    const addUserPlantData = (newUserPlantData) => {
        setUserPlantData([...userPlantData, newUserPlantData]);
    };

    const addGardener = (newGardener) => {
        setGardeners([...gardeners, newGardener]);
    };

    const submitReport = (newReport) => {
        setReport([...report, newReport]);
    };

    const handleViewPlantedVarieties = () => {
        // Implement the logic for viewing planted varieties
        console.log("View Planted Varieties button clicked");
    };

    const handleSelectVariety = (variety) => {
        setSelectedVariety(variety);
        if (variety) {
            setCurrentTab('My Plants');
        }
    };

    const handleSelectCrop = (crop) => {
        setSelectedCrop(crop);
        //setCurrentTab('My Plants');
        setCurrentTab('Crop Profile');
    };

    const handleGenerateReport = async (e) => {
        e.preventDefault();
        try {
            const response = await api.post('/report/generate-report', {
                gardenerID: currentGardener?.id,
                gardenerEmail: currentGardener?.email
            });
            console.log('Report generated successfully');
            alert('Report generated successfully.');
        } catch (err) {
            console.error('Failed to generate report:', err);
            const errorMessage = err.response?.data || 'An error occurred while generating the report.';
            alert(errorMessage);
        }
    };

    const handleUpdateGardener = async (updatedGardener) => {
        const requestBody = {
            name: updatedGardener.name,
            email: updatedGardener.email,
            phone: updatedGardener.phone,
            city: updatedGardener.city,
            province: updatedGardener.province,
            country: updatedGardener.country,
            locationID: updatedGardener.locationID,
            familysize: updatedGardener.familysize,
            experienceLevel: updatedGardener.experienceLevel,
            isNewUser: updatedGardener.isNewUser
        };

        try {
            await api.put(`/gardeners/${updatedGardener.id}`, requestBody);
            fetchCurrentGardener(isDemoMode); // Refresh gardener state after successful update
        } catch (err) {
            console.error('Failed to update gardener:', err);
        }
    };
    const handleAddPlant = async (plantData) => {
        const { plantID, plantType } = plantData;

        if (!plantID) {
            console.error('No plant selected.');
            alert('Please select a plant to add.');
            return;
        }

        const numericPlantID = parseInt(plantID, 10);

        const plantInList = plants.some(listplant => listplant.id === numericPlantID);
        if (plantInList) {
            console.error('Plant already exists in your list.');
            alert('Plant already exists in your list.');
            return;
        }

        const requestBody = {
            gardenerID: currentGardener.id,
            plantID,
            plantType
        };

        try {
            await api.post('/gardenerPlants/add', requestBody);
            fetchCurrentGardener(isDemoMode); // Refresh state after successful add
        } catch (err) {
            console.error('Failed to add plant:', err);
        }
    };

const handleRemovePlant = async (plantID) => {

  if (!plantID) {
    console.error('No plant selected.');
    alert('Please select a plant to remove.');
    return;
  }

  const numericPlantID = parseInt(plantID, 10);

  const plantInList = plants.some((listplant) => listplant.id === numericPlantID);
  if (!plantInList) {
    console.error('Plant is not in your current list.');
    alert('Plant is not in your current list.');
    return;
  }

  const varietiesExist = userPlantData.some((data) => data.plantID === numericPlantID);
  if (varietiesExist) {
    console.error('Cannot remove plant until all varieties are removed.');
    alert('Cannot remove plant until all varieties are removed.');
    return;
  }

  try {
    await api.delete(`/gardenerPlants/remove/${currentGardener.id}/${plantID}`);
    fetchCurrentGardener(isDemoMode); // Refresh state
  } catch (err) {
    console.error('Failed to remove plant:', err);
    alert('Failed to remove plant.');
  }
};

const handleAddUserPlant = async (userPlantData, pendingSeasonSelections) => {
  const { plantID, plantType } = userPlantData;
  try {
      const response = await api.post('/userplants/add', userPlantData);

      const newUserPlantID = response.data.userPlantID;

      // APPLY PENDING XREFS
      console.log('NEW USER PLANT ID:', newUserPlantID);
      console.log('PENDING REGION IDs:', pendingSeasonSelections);

      await Promise.all(
          pendingSeasonSelections.map(regionID =>
              api.post('/plantingRegion/add-season-xref', {
                  userPlantID: newUserPlantID,
                  plantingRegionID: regionID
              })
          )
      );
      fetchCurrentGardener(isDemoMode);
      handleSelectVariety({
          ...userPlantData,
          userPlantID: newUserPlantID,
          isNewVariety: false
      });
  } catch (err) {
    console.error('Error adding user plant:', err);
  }
};

const handleUpdateUserPlant = async (userplant) => {
  let response; // Declare the variable here to use it in both the try and catch blocks
  try {
    response = await api.put(`/userplants`, userplant); // Use the correct endpoint

    const data = response.data;
      fetchCurrentGardener(isDemoMode);
      setSelectedVariety(userplant);

  } catch (err) {
    // Update the error logging to handle text response
    console.error('Error updating user plant:', err);

    // Check if the response is not JSON, handle it accordingly
    if (err.response && typeof err.response.data === 'string') {
      console.error('Server response:', err.response.data);
    }
  }
};

const handleDeleteUserPlant = async (userplant) => {
  let response; // Declare the variable here to use it in both the try and catch blocks

  try {
      await api.delete(`/userplants/${userplant.userPlantID}`, {
          data: {
              scheduleTable: currentGardener.scheduleTable
          }
      });

      fetchCurrentGardener(isDemoMode);
      setSelectedVariety(null);

  } catch (err) {
    console.error('Error deleting user plant:', err);

    // Check if the response is not JSON, handle it accordingly
    if (err.response?.data?.error) {
    alert(err.response.data.error);
  } else {
    alert('Unable to delete this variety.');
  }
  }
};

const handleTryDemo = async () => {
    try {

    // Only sign in the demo user if no one is currently signed in
    if (!token) {
      const response = await api.post('/auth/login', {
        username: 'Demo',
        password: 'test'
      });
      const data = response.data;
      localStorage.setItem('token', data.token);
      setToken(data.token);
      setIsLoggedIn(true);
    }

    demoTriggeredRef.current = true;
    setIsDemoMode(true);
    const demoGardener = await fetchCurrentGardener(true);
    setCurrentTab('Home');
  } catch (err) {
    console.error('Failed to load demo gardener:', err);
    alert('Demo mode is currently unavailable.');
    }
};

const handleCancelDemo = async () => {
  demoTriggeredRef.current = false;
  setIsDemoMode(false);

  try {
    const base64Url = token?.split('.')[1];
    const base64 = base64Url?.replace(/-/g, '+').replace(/_/g, '/');
    const decodedToken = base64 ? JSON.parse(atob(base64)) : null;

    const isDemoUser = decodedToken?.userid === 'Demo' || decodedToken?.username === 'Demo';

    if (isDemoUser) {
      handleLogout();
      return;
    }
  } catch (err) {
    console.error('Failed to decode token during demo exit:', err);
  }

/*  const realGardener = await fetchCurrentGardener(false);
    if (realGardener?.isNewUser) {
        // brand new gardener then resume onboarding
        handleTabChange('Onboarding');
    } else {
        // existing gardener then go to Home
        handleTabChange('Home');
    }*/
};

const handleTabChange = (tab, triggeredBySystem = false) => {
  if (currentGardener?.isNewUser && !triggeredBySystem) {
    alert('Please finish onboarding before accessing other features.');
    return;
  }
  setCurrentTab(tab);
    };


    const isLandingPage = currentTab === 'LandingPage';
    const showLeftSidebar = false;
    const isForcedReset = currentTab === 'ForcedReset';

    let renderedTabContent;
    if (isLoggedIn) {
        if (user?.isSupplier) {
            return <SupplierSystem
                user={user}
                token={token}
                handleLogout={handleLogout}
            />;
        }
        if (user?.isFarmstand) {
            return <FarmstandSystem
                user={user}
                token={token}
                handleLogout={handleLogout}
            />;
        }
    }

    if (isLandingPage) {
        renderedTabContent = (
            <div className="tab-container landing-layout">
                <LandingPage
                    isLocked={isLocked}
                    credentials={credentials}
                    handleChange={handleChange}
                    handleRegister={handleRegister}
                    handleLogin={handleLogin}
                    handleResetPassword={handleResetPassword}
                    handleConfirmResetPassword={handleConfirmResetPassword}
                    errorMessage={errorMessage}
                    handleTryDemo={handleTryDemo}
                    isResetting={isResetting}
                />
                <LandingSidebar />
            </div>
        );
    } else if (currentTab === 'Home') {
        renderedTabContent = !token && !isDemoMode ? (
            <AuthForm
                credentials={credentials}
                handleChange={handleChange}
                handleRegister={handleRegister}
                handleLogin={handleLogin}
                handleResetPassword={handleResetPassword}
                handleConfirmResetPassword={handleConfirmResetPassword}
                errorMessage={errorMessage}
            />
        ) : (
            <HomeBaseForm
                    currentGardener={currentGardener}
                    setCurrentGardener={setCurrentGardener}
                    userPlantData={userPlantData}
                    handleTabChange={handleTabChange}
                    fetchCurrentGardener={fetchCurrentGardener}
                    deferredPrompt={deferredPrompt}
                    setDeferredPrompt={setDeferredPrompt}
                    isDemoMode={isDemoMode}
                    currentDate={currentDate}
                    isLockedOutMode={isLockedOutMode }
            />
        );
    } else if (currentTab === 'Gardener') {
        renderedTabContent = !token ? (
            <AuthForm
                credentials={credentials}
                handleChange={handleChange}
                handleRegister={handleRegister}
                handleLogin={handleLogin}
                handleResetPassword={handleResetPassword}
                handleConfirmResetPassword={handleConfirmResetPassword}
                errorMessage={errorMessage}
            />
        ) : currentGardener?.isNewUser ? (
            <GardenerOnboard
                handleTabChange={handleTabChange}
                handleUpdateGardener={handleUpdateGardener}
                handleAddPlant={handleAddPlant}
                currentDate={currentDate}
                deferredPrompt={deferredPrompt}
                setDeferredPrompt={setDeferredPrompt}
            />
        ) : (
            <AddGardenerForm
                handleTabChange={handleTabChange}
                onAddPlant={handleAddPlant}
                onRemovePlant={handleRemovePlant}
                onUpdateGardener={handleUpdateGardener}
                isDemoMode={isDemoMode}
            />
        );
    } else if (currentTab === 'My Plants') {
        renderedTabContent = !token ? (
            <AuthForm
                credentials={credentials}
                handleChange={handleChange}
                handleRegister={handleRegister}
                handleLogin={handleLogin}
                handleResetPassword={handleResetPassword}
                handleConfirmResetPassword={handleConfirmResetPassword}
                errorMessage={errorMessage}
            />
        ) : (
            <UserPlantsForm
                currentGardener={currentGardener}
                plants={plants}
                userPlantData={userPlantData}
                onAddUserPlant={handleAddUserPlant}
                onUpdateUserPlant={handleUpdateUserPlant}
                onDeleteUserPlant={handleDeleteUserPlant}
                onSelectVariety={handleSelectVariety}
                selectedVariety={selectedVariety}
                onSelectCrop={handleSelectCrop}
                selectedCrop={selectedCrop}
                isDemoMode={isDemoMode}
                currentDate={currentDate}

            />
        );
    } else if (currentTab === 'Crop Profile') {
        renderedTabContent = !token ? (
            <AuthForm
                credentials={credentials}
                handleChange={handleChange}
                handleRegister={handleRegister}
                handleLogin={handleLogin}
                handleResetPassword={handleResetPassword}
                handleConfirmResetPassword={handleConfirmResetPassword}
                errorMessage={errorMessage}
            />
        ) : (
            <CropProfile
                    currentGardener={currentGardener}
                    plants={plants}
                    userPlantData={userPlantData}
                    onAddUserPlant={handleAddUserPlant}
                    onUpdateUserPlant={handleUpdateUserPlant}
                    onDeleteUserPlant={handleDeleteUserPlant}
                    onSelectVariety={handleSelectVariety}
                    selectedVariety={selectedVariety}
                    onSelectCrop={handleSelectCrop}
                    selectedCrop={selectedCrop}
                    isDemoMode={isDemoMode}
                    currentDate={currentDate}
                    handleAddPlant={handleAddPlant}
                    fetchCurrentGardener={fetchCurrentGardener }

            />
        );
    } else if (currentTab === 'Garden Journal') {
        renderedTabContent = (
            <GardenJournal
                currentGardener={currentGardener}
                plants={plants}
                userPlantData={userPlantData}
                isDemoMode={isDemoMode}
            />
        );
    } else if (currentTab === 'Season Planner') {
        renderedTabContent = (
            <ThreeMonthTaskPlanner
                currentGardener={currentGardener}
                plants={plants}
                handleTabChange={handleTabChange}
                isDemoMode={isDemoMode}
                currentDate={currentDate}
            />
        );
    } else if (currentTab === 'Preserve') {
        renderedTabContent = (
            <Preserve
                preserveUserKey={currentGardener?.userid }
                handleTabChange={handleTabChange}
                currentDate={currentDate}
                handleTryDemo={handleTryDemo}
                handleCancelDemo={handleCancelDemo}
                isDemoMode={isDemoMode}
                onOpenHelp={() => setShowHelp(true)}
                isLockedOutMode={isLockedOutMode}
            />
        );
    
    
    
} else if (currentTab === 'ForcedReset') {
    renderedTabContent = (
        <div className="tab-container landing-layout">
            <div className="auth-form-container">
                <h2 className="auth-title">You enter a new password</h2>

                <input
                    type="password"
                    className="auth-input"
                    placeholder="Enter new password"
                    value={forcedPassword}
                    onChange={(e) => setForcedPassword(e.target.value)}
                />

                <button className="button" onClick={handleForcedPasswordUpdate}>
                    Save New Password
                </button>

                {errorMessage && <p className="auth-error">{errorMessage}</p>}
            </div>
        </div>
    );
    
    } else if (currentTab === 'Orders') {
        renderedTabContent = 
        <Orders
                currentGardener={currentGardener}
                isDemoMode={isDemoMode}
        />;
    } else if (currentTab === 'About') {
        renderedTabContent = <AboutContent />;
    } else if (currentTab === 'Admin') {
        renderedTabContent = (
            <AdminDashboard
                currentGardener={currentGardener}
                token={token}
                handleTabChange={handleTabChange}
            />
        );
    }
  

  return (
    <div className="App">

      {(isLandingPage || isForcedReset) && (
          <LandingHeader onOpenHelp={() => setShowHelp(true)} />)}
          {showHelp && (
              <div className="modal-overlay">
                  <div className="modal-content">
                      <HelpPage />
                      <button className="close-button" onClick={() => setShowHelp(false)}>
                          Close
                      </button>
                  </div>
              </div>
          )}

          {!isLandingPage && !isForcedReset && currentGardener && (
        <Header
                  currentTab={currentTab}
                  handleTabChange={handleTabChange}
                  handleLogout={handleLogout}
                  handleGenerateReport={handleGenerateReport}
                  currentGardener={currentGardener}
                  setCurrentGardener={setCurrentGardener}
                  isDemoMode={isDemoMode}
                  handleCancelDemo={handleCancelDemo}
                  isNewUserMode={currentGardener?.isNewUser === true}
                  setIsLockedOutMode={setIsLockedOutMode }
                  fetchCurrentGardener={fetchCurrentGardener}

        />
      )}

      <div className={`container ${isLandingPage ? 'no-header' : ''}`}>
        {!isLandingPage && !isForcedReset && showLeftSidebar && (
          <Sidebar
            currentTab={currentTab}
            isLoggedIn={isLoggedIn}
            currentGardener={currentGardener}
          />
        )}

        <div className="main-content">
          {(isLandingPage || isForcedReset) ? (
            renderedTabContent
          ) : (
             <div className={`tab-container ${isDemoMode ? 'demo-mode-offset' : ''}`}>            
                              {renderedTabContent}
                              {currentTab !== 'Preserve' && (
                                  <SidebarRight
                                      currentGardener={currentGardener}
                                      plants={plants}
                                      userPlantData={userPlantData}
                                      isLoggedIn={isLoggedIn}
                                      onSelectVariety={handleSelectVariety}
                                      selectedVariety={selectedVariety}
                                      onSelectCrop={handleSelectCrop}
                                      selectedCrop={selectedCrop}
                                      onViewPlantedVarieties={handleViewPlantedVarieties}
                                      handleAddPlant={handleAddPlant}
                                      handleRemovePlant={handleRemovePlant}
                                      handleTryDemo={handleTryDemo}
                                      handleCancelDemo={handleCancelDemo}
                                      isDemoMode={isDemoMode}
                                      handleTabChange={handleTabChange}
                                      onOpenHelp={() => setShowHelp(true)}
                                      isLockedOutMode={isLockedOutMode}
                                  />
                              )}
            </div>
                  )}

                  {/* Mount the tour globally so it overlays everything */}
                  <DemoTour
                      key={runTour ? 'tour-on' : 'tour-off'}
                      runTour={runTour}
                      setRunTour={setRunTour}
                      handleTabChange={handleTabChange} />

        </div>
      </div>
    </div>
  );
  }
export default App;